import { CheckSelectionItemFilter } from '~/features/catalog/components/filter/CheckSelectionItemFilter';
import { useCallback, useState } from 'react';
import { defaultPageSize } from '~/features/catalog/mappers/catalogSearchQueryMapper';
import { cn } from '~/common/utils/styleUtil';
import type { FilterDefinition } from '~/features/catalog/types/filterDefinition';
import type { ISearchRequestPagingDTOData } from '~/dtos/ProdegaApiWebshop-api-typings-generated';
import { useScreenSize } from '~/common/hooks/useScreenSize';
import { Button } from '~/common/components/ui/button';
import { useTranslation } from 'react-i18next';
import { LucideListFilter } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '~/common/components/ui/dialog';
import { Badge } from '~/common/components/ui/badge';
import { FilterReset } from '~/features/catalog/components/filter/FilterReset';
import { SalesOrderSetFilter } from '~/features/catalog/components/filter/SalesOrderSetFilter';
import { MultiSelectionItemFilter } from '~/features/catalog/components/filter/MultiSelectionItemFilter.tsx';

interface SearchFilterProps {
  filters: FilterDefinition[];
  totalCount: number | undefined;
  liveSearchRequest: ISearchRequestPagingDTOData;
  onApplyFilter: (searchRequest: ISearchRequestPagingDTOData) => void;
  className?: string;
  area: string;
  disabled?: boolean;
}

export function SearchFilter({
  onApplyFilter,
  liveSearchRequest,
  className,
  filters,
  area,
  disabled
}: Readonly<SearchFilterProps>) {
  const { isDesktop, isMobile } = useScreenSize();
  const { t } = useTranslation();
  const [showFilterDialog, setShowFilterDialog] = useState(false);

  const handleApplyFilter = useCallback(
    <T extends keyof ISearchRequestPagingDTOData>(filterName: T, value: ISearchRequestPagingDTOData[T]) => {
      if (!disabled) {
        onApplyFilter({ ...liveSearchRequest, [filterName]: value, page: 0, pageSize: defaultPageSize });
      }
    },
    [liveSearchRequest, onApplyFilter, disabled]
  );

  const getFilterComponent = useCallback(
    (filter: FilterDefinition, className?: string) => {
      if (filter.type === 'check') {
        return (
          <CheckSelectionItemFilter
            className={className}
            key={filter.searchFilterKey}
            checked={!!liveSearchRequest[filter.searchFilterKey]}
            onApplyFilter={checked => {
              handleApplyFilter(filter.searchFilterKey, checked ? true : (filter.emptyValue ?? undefined));
            }}
            filterTitle={filter.label}
            disabled={disabled}
          />
        );
      }
      return (
        <MultiSelectionItemFilter
          key={filter.searchFilterKey}
          searchKey={filter.searchKey}
          filterTitle={filter.label}
          liveSelectedItems={filter.selectedFilterItems ?? []}
          onApplyFilter={selectedItemIds => {
            if (isMobile) {
              setShowFilterDialog(false);
            }
            handleApplyFilter(filter.searchFilterKey, selectedItemIds);
          }}
          onBack={
            isMobile
              ? selectedItemIds => {
                  handleApplyFilter(filter.searchFilterKey, selectedItemIds);
                }
              : undefined
          }
          canSearch={filter.canSearch ?? true}
          refreshItemsOnEverySelection={filter.refreshItemsOnEverySelection}
          itemRestUrl={filter.restUrl}
          area={area}
          disabled={disabled}
        />
      );
    },
    [area, disabled, handleApplyFilter, isMobile, liveSearchRequest]
  );

  const activeCount = filters.filter(x => x.active).length;
  const activeNotFeaturedCount = filters.filter(x => x.active && !x.featured).length;

  if (isDesktop) {
    return (
      <div className={cn('', className)}>
        <div className={'flex gap-4'}>
          <SalesOrderSetFilter />
          {filters.filter(x => x.featured && x.type === 'check').map(x => getFilterComponent(x, 'h-8'))}
        </div>
        <div className={'grid grid-cols-[repeat(auto-fit,minmax(260px,1fr))] gap-2 mt-2'}>
          {filters.filter(x => x.featured && x.type === 'multi').map(x => getFilterComponent(x))}
        </div>
        <div className="grid grid-cols-[repeat(auto-fit,minmax(260px,1fr))] gap-2">
          {filters.filter(x => !x.featured).map(x => getFilterComponent(x, 'h-10'))}
        </div>
      </div>
    );
  }

  return (
    <div className={cn(className)}>
      <div className={'flex mb-2 gap-4'}>
        <SalesOrderSetFilter />
        {filters.filter(x => x.featured).map(x => getFilterComponent(x))}
      </div>
      <Dialog open={showFilterDialog} onOpenChange={setShowFilterDialog}>
        <DialogTrigger asChild>
          <Button className={'w-full flex justify-between'} variant={'outline'}>
            {t('Filter öffnen')}
            <div className={'flex gap-2'}>
              {activeNotFeaturedCount > 0 && <Badge>{activeNotFeaturedCount}</Badge>}
              <LucideListFilter />
            </div>
          </Button>
        </DialogTrigger>
        <DialogContent showFullscreenOnMobile className={'flex flex-col p-0'} data-testid={'searchfilter-dialog'}>
          <DialogHeader>
            <DialogTitle className={'flex gap-4 p-4 pb-0 pt-4'}>
              <div>{t('Filter')}</div>
              {activeCount > 0 && <Badge>{activeCount}</Badge>}
            </DialogTitle>
          </DialogHeader>
          <div className={'grow overflow-y-scroll pt-2'}>
            <div className={'flex gap-4 px-4 desktop:px-0'}>
              {filters.filter(x => x.featured && x.type === 'check').map(x => getFilterComponent(x))}
            </div>
            <div className={'grid grid-cols-[repeat(auto-fit,minmax(260px,1fr))] gap-2 mt-2'}>
              {filters.filter(x => x.featured && x.type === 'multi').map(x => getFilterComponent(x))}
            </div>
            <div className="grid grid-cols-[repeat(auto-fit,minmax(260px,1fr))]">
              {filters.filter(x => !x.featured).map(x => getFilterComponent(x, 'grow min-h-14 border-b px-4'))}
            </div>
          </div>
          <DialogFooter className={'flex gap-2 flex-row justify-between items-center p-2'}>
            <FilterReset filters={filters} liveSearchRequest={liveSearchRequest} />
            <Button
              className={'w-full'}
              onClick={() => setShowFilterDialog(false)}
              data-testid={'searchfilter-dialog-ok-button'}
            >
              {t('Produkte anzeigen')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
